import { getAdaptivePopoverWidthCSS } from 'theme-utils';
import type { DialogSizes } from './types';

/**
 * A mapping of dialog size names to their corresponding widths.
 * These values are used to maintain consistent dialog sizes across the application.
 *
 * @example
 * ```tsx
 * // Get the width for a medium dialog
 * const mediumWidth = DIALOG_SIZES.medium; // `calc(var(--spaces-64) * 8)` // 512px
 *
 * // Get the width for a fullscreen dialog
 * const fullscreenWidth = DIALOG_SIZES.fullscreen; // '100%'
 * ```
 */
export const DIALOG_SIZES: Record<DialogSizes, number | string> = {
  'x-small': getAdaptivePopoverWidthCSS({ multiplier: 5 }),
  small: getAdaptivePopoverWidthCSS({ multiplier: 6 }),
  medium: getAdaptivePopoverWidthCSS({ multiplier: 8 }),
  large: getAdaptivePopoverWidthCSS({ multiplier: 12 }),
  'x-large': getAdaptivePopoverWidthCSS({ multiplier: 16 }),
  fullscreen: '100%',
} as const;

/**
 * Converts a dialog size to its CSS width value.
 * For numeric sizes, appends 'px' to the value.
 * For 'fullscreen', returns '100%'.
 *
 * @param size - The dialog size to normalize
 * @returns The CSS width value for the given size
 *
 * @example
 * ```tsx
 * const width = getNormalizedDialogSize('medium'); // `calc(var(--spaces-64) * 8)` // 512px
 * const fullscreenWidth = getNormalizedDialogSize('fullscreen'); // '100%'
 * ```
 */
export const getNormalizedDialogSize = (size: DialogSizes) => {
  return DIALOG_SIZES[size];
};
